import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { 
  Calendar,
  Clock, 
  TrendingUp, 
  BarChart2, 
  Award,
  BookOpen
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { getAllMockTestResults } from "@/lib/mockTestResultsUtils";

interface MockTestAnalytics {
  totalTests: number;
  averageScore: number;
  bestScore: number;
  recentTests: any[];
  categoryPerformance: any[];
  difficultyPerformance: any[];
  timeDistribution: any[];
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#FF6B6B'];

const MockTestAnalytics: React.FC = () => {
  const [analytics, setAnalytics] = useState<MockTestAnalytics | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [timeframe, setTimeframe] = useState<'week' | 'month' | 'all'>('month');

  useEffect(() => {
    fetchAnalytics();
  }, [timeframe]);

  const fetchAnalytics = async () => {
    setLoading(true);
    
    try {
      // Get all mock test results
      const results = await getAllMockTestResults(timeframe);
      
      if (!results || results.length === 0) {
        setAnalytics({
          totalTests: 0,
          averageScore: 0,
          bestScore: 0,
          recentTests: [],
          categoryPerformance: [],
          difficultyPerformance: [],
          timeDistribution: []
        });
        setLoading(false);
        return;
      }
      
      // Calculate analytics
      const totalTests = results.length;
      const averageScore = results.reduce((sum, test) => sum + (test.percentage || 0), 0) / totalTests;
      const bestScore = Math.max(...results.map(test => test.percentage || 0));
      
      // Recent tests data
      const recentTests = results.slice(0, 5).map(test => {
        const completedDate = new Date(test.completedAt || test.created_at);
        const testConfig = test.testConfig || {};
        const category = testConfig.category || "General";
        
        return {
          id: test.id,
          date: completedDate.toLocaleDateString(),
          category: category,
          score: test.percentage || 0,
          questions: test.totalPossibleScore || test.total_questions || 0,
          correct: test.score || test.correct_answers || 0
        };
      });
      
      // Aggregate category performance
      const categoryData: Record<string, { correct: number, incorrect: number, unattempted: number, total: number }> = {};
      
      results.forEach(test => {
        const testConfig = test.testConfig || {};
        const category = testConfig.category || "General";
        
        if (!categoryData[category]) {
          categoryData[category] = { 
            correct: 0, 
            incorrect: 0, 
            unattempted: 0, 
            total: 0 
          };
        }
        
        const correctAnswers = test.score || test.correct_answers || 0;
        const totalQuestions = test.totalPossibleScore || test.total_questions || 0;
        const answered = Object.keys(test.submittedAnswers || {}).length;
        
        categoryData[category].correct += correctAnswers;
        categoryData[category].incorrect += answered - correctAnswers;
        categoryData[category].unattempted += totalQuestions - answered;
        categoryData[category].total += totalQuestions;
      });
      
      const categoryPerformance = Object.entries(categoryData).map(([category, data]) => ({
        name: category,
        correct: data.correct,
        incorrect: data.incorrect,
        unattempted: data.unattempted,
        percentage: data.total > 0 ? Math.round((data.correct / data.total) * 100) : 0
      }));
      
      // Aggregate difficulty performance
      const difficultyData = {
        easy: { correct: 0, incorrect: 0, total: 0 },
        medium: { correct: 0, incorrect: 0, total: 0 },
        hard: { correct: 0, incorrect: 0, total: 0 }
      };
      
      results.forEach(test => {
        if (test.questionResults) {
          test.questionResults.forEach((qr: any) => {
            const question = test.testConfig?.questions.find((q: any) => q.id === qr.questionId);
            if (question) {
              const difficulty = (question.difficulty || "medium").toLowerCase();
              
              if (!difficultyData[difficulty as keyof typeof difficultyData]) {
                difficultyData[difficulty as keyof typeof difficultyData] = { correct: 0, incorrect: 0, total: 0 };
              }
              
              difficultyData[difficulty as keyof typeof difficultyData].total += 1;
              
              if (qr.isCorrect) {
                difficultyData[difficulty as keyof typeof difficultyData].correct += 1;
              } else {
                difficultyData[difficulty as keyof typeof difficultyData].incorrect += 1;
              }
            }
          });
        }
      });
      
      const difficultyPerformance = Object.entries(difficultyData).map(([difficulty, data]) => ({
        name: difficulty.charAt(0).toUpperCase() + difficulty.slice(1),
        correct: data.correct,
        incorrect: data.incorrect,
        accuracy: data.total > 0 ? Math.round((data.correct / data.total) * 100) : 0,
        value: data.total
      }));
      
      // Time distribution (bucketed)
      const timeBuckets = {
        "< 1 hour": 0,
        "1-1.5 hours": 0,
        "1.5-2 hours": 0,
        "Full Time": 0
      };
      
      results.forEach(test => {
        const minutes = (test.timeSpent || test.time_spent || 0) / 60;
        
        if (minutes < 60) {
          timeBuckets["< 1 hour"]++;
        } else if (minutes < 90) {
          timeBuckets["1-1.5 hours"]++;
        } else if (minutes < 120) {
          timeBuckets["1.5-2 hours"]++;
        } else {
          timeBuckets["Full Time"]++;
        }
      });
      
      const timeDistribution = Object.entries(timeBuckets).map(([label, count]) => ({
        name: label,
        value: count
      }));
      
      setAnalytics({
        totalTests,
        averageScore,
        bestScore,
        recentTests,
        categoryPerformance,
        difficultyPerformance,
        timeDistribution
      });
    } catch (error) {
      console.error('Error fetching mock test analytics:', error);
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  const renderTimeframeSelector = () => (
    <div className="flex space-x-2 mb-4">
      <Button 
        variant={timeframe === 'week' ? "default" : "outline"} 
        size="sm" 
        onClick={() => setTimeframe('week')}
      >
        Last Week
      </Button>
      <Button 
        variant={timeframe === 'month' ? "default" : "outline"} 
        size="sm" 
        onClick={() => setTimeframe('month')}
      >
        Last Month
      </Button>
      <Button 
        variant={timeframe === 'all' ? "default" : "outline"} 
        size="sm" 
        onClick={() => setTimeframe('all')}
      >
        All Time
      </Button>
    </div>
  );

  if (loading) {
    return (
      <div className="space-y-4 p-4">
        {renderTimeframeSelector()}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="p-4">
              <Skeleton className="h-4 w-1/3 mb-2" />
              <Skeleton className="h-8 w-1/2 mb-2" />
              <Skeleton className="h-4 w-2/3" />
            </Card>
          ))}
        </div>
        <Card className="p-4 mb-6">
          <Skeleton className="h-5 w-40 mb-4" />
          <Skeleton className="h-[300px] w-full" />
        </Card>
      </div>
    );
  }

  if (!analytics || analytics.totalTests === 0) {
    return (
      <div className="p-4">
        {renderTimeframeSelector()}
        <Card className="p-6 text-center">
          <BookOpen className="h-12 w-12 mx-auto mb-3 text-muted-foreground" />
          <h3 className="text-lg font-medium mb-2">No Mock Test Data Available</h3>
          <p className="text-muted-foreground mb-4">
            You haven't completed any mock tests in the selected timeframe.
          </p>
          <Button onClick={() => window.location.href = '/mock-test-generator'}>
            Take a Mock Test
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4">
      {renderTimeframeSelector()}
      
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="p-4 flex items-center">
          <div className="p-3 rounded-full bg-blue-100 mr-4">
            <BarChart2 className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Total Tests</p>
            <h3 className="text-2xl font-bold">{analytics.totalTests}</h3>
            <p className="text-sm">{timeframe === 'week' ? 'This week' : timeframe === 'month' ? 'This month' : 'All time'}</p>
          </div>
        </Card>
        
        <Card className="p-4 flex items-center">
          <div className="p-3 rounded-full bg-green-100 mr-4">
            <TrendingUp className="h-6 w-6 text-green-600" />
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Average Score</p>
            <h3 className="text-2xl font-bold">{Math.round(analytics.averageScore)}%</h3>
            <p className="text-sm">Across all tests</p>
          </div>
        </Card>
        
        <Card className="p-4 flex items-center">
          <div className="p-3 rounded-full bg-amber-100 mr-4">
            <Award className="h-6 w-6 text-amber-600" />
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Best Score</p>
            <h3 className="text-2xl font-bold">{analytics.bestScore}%</h3>
            <p className="text-sm">Your highest achievement</p>
          </div>
        </Card>
      </div>
      
      {/* Detailed Analytics */}
      <Tabs defaultValue="overview" className="mb-8">
        <TabsList className="mb-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="categories">Categories</TabsTrigger>
          <TabsTrigger value="difficulty">Difficulty</TabsTrigger>
          <TabsTrigger value="time">Time Analysis</TabsTrigger>
        </TabsList>
        
        <TabsContent value="overview" className="mt-0">
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Recent Mock Tests</h3>
            
            {analytics.recentTests.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Score</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Questions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {analytics.recentTests.map((test) => (
                      <tr key={test.id} 
                        className="hover:bg-gray-50 cursor-pointer" 
                        onClick={() => window.location.href = `/mock-test-results/${test.id}`}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{test.date}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">{test.category}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <span className={`text-sm font-medium ${
                              test.score >= 70 ? 'text-green-600' : 
                              test.score >= 40 ? 'text-amber-600' : 
                              'text-red-600'
                            }`}>
                              {test.score}%
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {test.correct}/{test.questions}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-4">No recent tests available</p>
            )}
          </Card>
        </TabsContent>
        
        <TabsContent value="categories" className="mt-0">
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Performance by Category</h3>
            
            {analytics.categoryPerformance.length > 0 ? (
              <>
                <div className="mb-6">
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                      data={analytics.categoryPerformance}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip formatter={(value, name) => [`${value}`, name === 'percentage' ? 'Accuracy' : name]} />
                      <Legend />
                      <Bar dataKey="correct" name="Correct" stackId="a" fill="#10b981" />
                      <Bar dataKey="incorrect" name="Incorrect" stackId="a" fill="#ef4444" />
                      <Bar dataKey="unattempted" name="Unattempted" stackId="a" fill="#9ca3af" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                
                <div className="space-y-4">
                  {analytics.categoryPerformance.map((category) => (
                    <div key={category.name} className="border-b pb-3">
                      <div className="flex justify-between mb-1">
                        <span className="font-medium">{category.name}</span>
                        <span className={`font-medium ${
                          category.percentage >= 70 ? 'text-green-600' : 
                          category.percentage >= 40 ? 'text-amber-600' : 
                          'text-red-600'
                        }`}>{category.percentage}%</span>
                      </div>
                      <Progress value={category.percentage} className="h-2 mb-1" />
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>Correct: {category.correct}</span>
                        <span>Incorrect: {category.incorrect}</span>
                        <span>Unattempted: {category.unattempted}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-muted-foreground text-center py-4">No category data available</p>
            )}
          </Card>
        </TabsContent>
        
        <TabsContent value="difficulty" className="mt-0">
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Performance by Difficulty</h3>
            
            {analytics.difficultyPerformance.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={analytics.difficultyPerformance}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                        nameKey="name"
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      >
                        {analytics.difficultyPerformance.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value, name, props) => [`${value} questions`, props.payload.name]} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                
                <div className="space-y-4">
                  {analytics.difficultyPerformance.map((difficulty, index) => (
                    <div key={difficulty.name} className="border-b pb-3">
                      <div className="flex justify-between mb-1">
                        <div className="flex items-center">
                          <div 
                            className="w-3 h-3 rounded-full mr-2" 
                            style={{ backgroundColor: COLORS[index % COLORS.length] }}
                          ></div>
                          <span className="font-medium">{difficulty.name}</span>
                        </div>
                        <span className={`font-medium ${
                          difficulty.accuracy >= 70 ? 'text-green-600' : 
                          difficulty.accuracy >= 40 ? 'text-amber-600' : 
                          'text-red-600'
                        }`}>{difficulty.accuracy}%</span>
                      </div>
                      <Progress value={difficulty.accuracy} className="h-2 mb-1" />
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>Questions: {difficulty.value}</span>
                        <span>Correct: {difficulty.correct} / Incorrect: {difficulty.incorrect}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-4">No difficulty data available</p>
            )}
          </Card>
        </TabsContent>
        
        <TabsContent value="time" className="mt-0">
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Time Distribution</h3>
            
            {analytics.timeDistribution.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={analytics.timeDistribution}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                        nameKey="name"
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      >
                        {analytics.timeDistribution.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value, name, props) => [`${value} tests`, props.payload.name]} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                
                <div className="space-y-4">
                  <div className="border-b pb-3">
                    <h4 className="font-medium mb-3">Time Completion Analysis</h4>
                    <p className="text-sm text-muted-foreground mb-4">
                      This chart shows how much time you typically use to complete your mock tests.
                    </p>
                    
                    <ul className="space-y-2">
                      {analytics.timeDistribution.map((time, index) => (
                        <li key={index} className="flex items-center">
                          <div 
                            className="w-3 h-3 rounded-full mr-2" 
                            style={{ backgroundColor: COLORS[index % COLORS.length] }}
                          ></div>
                          <span className="text-sm">
                            {time.name}: <strong>{time.value}</strong> tests
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="pt-2">
                    <h4 className="font-medium mb-2">Time Management Tips</h4>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li className="flex items-start">
                        <Clock className="h-4 w-4 mr-2 mt-0.5 text-blue-500" />
                        <span>Aim to spend about 1 minute per question on easy questions</span>
                      </li>
                      <li className="flex items-start">
                        <Clock className="h-4 w-4 mr-2 mt-0.5 text-blue-500" />
                        <span>For medium difficulty, allow 1.5 minutes per question</span>
                      </li>
                      <li className="flex items-start">
                        <Clock className="h-4 w-4 mr-2 mt-0.5 text-blue-500" />
                        <span>Difficult questions may require up to 2 minutes each</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-4">No time distribution data available</p>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MockTestAnalytics;
